"use client";

import { useEffect, useMemo, useState } from "react";
import { AlertCircle, Check, Loader2, Send } from "lucide-react";
import { FileUpload } from "./file-upload";
import { HeardAboutUsSelect, useHeardAboutUsOptions } from "./heard-about-us-select";
import { getRecaptchaToken } from "@/lib/recaptcha";
import {
  getContactFieldAutoComplete,
  prefillMarketingFormValues,
  saveContactInfoFromSubmittedValues,
  subscribeToContactInfoChange,
} from "@/lib/contact-prefill";
import {
  fetchPublicForm,
  type MarketingFormField,
  type MarketingFormOptIn,
  type MarketingFormSubmissionPayload,
  type PreloadedForm,
  type PublicMarketingForm,
  type SubmissionContext,
  submitForm,
  submitFormWithFiles,
} from "@/lib/api/forms";

interface BuiltInMarketingFormProps {
  slug: string;
  endpoint?: string;
  className?: string;
  submitLabel?: string;
  successLabel?: string;
  submittingLabel?: string;
  context?: Record<string, string | boolean | number | null | undefined>;
  /** The page's PageContext lead, sent as `attribution.page`. */
  page?: SubmissionContext["page"];
  intro?: React.ReactNode;
  dark?: boolean;
  enablePhotoUpload?: boolean;
  photoHelpText?: string;
  onSuccessResetMs?: number;
  includeMarketingUseDisclosure?: boolean;
  /**
   * The definition and options, fetched by the server page with
   * `preloadForm`, so the form renders at full height in the page's HTML.
   * Without them the form loads in the browser.
   */
  preloaded?: PreloadedForm;
}

type FieldValue = string | string[] | boolean;

export const MARKETING_USE_DISCLOSURE_TEXT =
  "I agree to receive marketing communications from Electrified Marina, including emails, calls, and text messages at the email address and phone number I provided. Message and data rates may apply. Consent is not a condition of purchase. I can opt out at any time.";

export function BuiltInMarketingForm({
  slug,
  endpoint = slug,
  className = "",
  submitLabel = "Submit",
  successLabel = "Submitted",
  submittingLabel = "Submitting...",
  context = {},
  page,
  intro,
  dark = false,
  enablePhotoUpload = false,
  photoHelpText = "Photos help us diagnose faster (optional)",
  onSuccessResetMs = 4000,
  includeMarketingUseDisclosure,
  preloaded,
}: BuiltInMarketingFormProps) {
  const initialForm = preloaded?.form;
  const [form, setForm] = useState<PublicMarketingForm | null>(initialForm ?? null);
  const [loadError, setLoadError] = useState(false);
  const [values, setValues] = useState<Record<string, FieldValue>>(() => initialValues(initialForm?.fields ?? []));
  const [marketingUseConsent, setMarketingUseConsent] = useState(false);
  const [selectedMarketingOptIns, setSelectedMarketingOptIns] = useState<number[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const heardAboutUsOptions = useHeardAboutUsOptions(preloaded?.heardAboutUs);
  const [selfReportedSource, setSelfReportedSource] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let active = true;
    // A definition from the server skips the fetch. Saved contact details
    // live in the browser, so either way they fill in after hydration.
    (initialForm ? Promise.resolve(initialForm) : fetchPublicForm(slug))
      .then((loaded) => {
        if (!active) return;
        const loadedFields = loaded?.fields ?? [];
        setForm(loaded);
        setValues(prefillMarketingFormValues(loadedFields, initialValues(loadedFields)));
        setSelectedMarketingOptIns([]);
      })
      .catch(() => {
        if (!active) return;
        setLoadError(true);
      });
    return () => {
      active = false;
    };
  }, [slug, initialForm]);

  const fields = useMemo(() => form?.fields ?? [], [form?.fields]);
  // "How did you hear about us?" is the last question, ahead of any consent
  // checkboxes the form definition ends with.
  let heardAboutUsIndex = fields.length;
  while (heardAboutUsIndex > 0 && fields[heardAboutUsIndex - 1].field_type === "consent") {
    heardAboutUsIndex--;
  }

  useEffect(() => {
    if (!form) return undefined;

    return subscribeToContactInfoChange(() => {
      setValues((current) => prefillMarketingFormValues(form.fields, current));
    });
  }, [form]);

  const showMarketingUseDisclosure =
    includeMarketingUseDisclosure ??
    Boolean(
      form?.include_marketing_use_disclosure ??
        form?.marketing_use_disclosure_enabled ??
        form?.marketing_use_disclosure,
    );
  const marketingUseDisclosureText =
    form?.marketing_use_disclosure_text?.trim() || MARKETING_USE_DISCLOSURE_TEXT;
  const marketingOptIns = form?.marketing_opt_ins ?? [];
  const missingRequiredMarketingOptIn = marketingOptIns.some(
    (optIn) => optIn.required && !selectedMarketingOptIns.includes(optIn.topic_id),
  );
  const inputClass = dark
    ? "w-full px-4 py-3 rounded-xl bg-white/10 border border-white/15 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-em-blue/60 focus:border-em-blue transition-all"
    : "w-full px-4 py-3 rounded-xl bg-secondary border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-em-blue/50 focus:border-em-blue transition-all";

  const updateValue = (key: string, value: FieldValue) => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg("");

    const hp = e.currentTarget.querySelector<HTMLInputElement>('[name="website"]');
    if (hp?.value) {
      setStatus("success");
      return;
    }

    try {
      const recaptcha_token = await getRecaptchaToken(`submit_${endpoint.replace(/[^a-z0-9_]/gi, "_")}`);
      const payload: MarketingFormSubmissionPayload = {
        ...values,
        ...context,
        marketing_opt_ins: selectedMarketingOptIns,
        source_url: window.location.href,
        recaptcha_token,
      };
      if (showMarketingUseDisclosure) {
        payload.marketing_use_consent = marketingUseConsent;
        payload.marketing_use_disclosure_text = marketingUseDisclosureText;
      }
      const result = enablePhotoUpload
        ? await submitFormWithFiles(endpoint, payload, files, { selfReportedSource, page })
        : await submitForm(endpoint, payload, { selfReportedSource, page });

      if (result.success) {
        setStatus("success");
        saveContactInfoFromSubmittedValues(values, fields);
        setValues(initialValues(fields));
        setMarketingUseConsent(false);
        setSelectedMarketingOptIns([]);
        setFiles([]);
        setSelfReportedSource("");
        if (onSuccessResetMs > 0) {
          setTimeout(() => setStatus("idle"), onSuccessResetMs);
        }
      } else {
        setErrorMsg(result.message);
        setStatus("error");
      }
    } catch {
      setErrorMsg("Unable to send. Please try again or call us directly.");
      setStatus("error");
    }
  };

  const renderField = (field: MarketingFormField) => (
    <div key={field.id} className={field.field_type === "textarea" || field.field_type === "consent" || field.field_type === "checkbox" ? "sm:col-span-2" : ""}>
      <DynamicField
        field={field}
        value={values[field.field_key]}
        inputClass={inputClass}
        dark={dark}
        onChange={(value) => updateValue(field.field_key, value)}
      />
    </div>
  );

  if (loadError) {
    return (
      <p className={`flex items-center gap-2 text-sm text-red-500 ${className}`}>
        <AlertCircle className="h-4 w-4 shrink-0" />
        Unable to load this form.
      </p>
    );
  }

  if (!form) {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="h-11 animate-pulse rounded-xl bg-secondary" />
        <div className="h-11 animate-pulse rounded-xl bg-secondary" />
        <div className="h-28 animate-pulse rounded-xl bg-secondary" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`space-y-5 ${className}`}>
      <input type="text" name="website" autoComplete="off" className="hidden" tabIndex={-1} aria-hidden="true" />
      {intro}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {fields.slice(0, heardAboutUsIndex).map(renderField)}
        {heardAboutUsOptions.length > 0 && (
          <div>
            <HeardAboutUsSelect
              options={heardAboutUsOptions}
              value={selfReportedSource}
              className={inputClass}
              onChange={setSelfReportedSource}
            />
          </div>
        )}
        {fields.slice(heardAboutUsIndex).map(renderField)}
      </div>

      {enablePhotoUpload && (
        <div>
          <p className={`mb-2 text-sm ${dark ? "text-white/65" : "text-muted-foreground"}`}>
            {photoHelpText}
          </p>
          <FileUpload maxFiles={5} onChange={setFiles} />
        </div>
      )}

      {status === "error" && errorMsg && (
        <p className="flex items-center gap-2 text-sm text-red-500">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {errorMsg}
        </p>
      )}

      {showMarketingUseDisclosure && (
        <MarketingUseDisclosure
          checked={marketingUseConsent}
          dark={dark}
          text={marketingUseDisclosureText}
          onChange={setMarketingUseConsent}
        />
      )}

      <MarketingOptIns
        optIns={marketingOptIns}
        selectedTopicIds={selectedMarketingOptIns}
        dark={dark}
        onChange={setSelectedMarketingOptIns}
      />

      <button
        type="submit"
        disabled={status === "submitting" || status === "success" || missingRequiredMarketingOptIn}
        className="em-button em-button--primary em-button--lg w-full"
      >
        {status === "submitting" ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {submittingLabel}
          </>
        ) : status === "success" ? (
          <>
            <Check className="h-4 w-4" />
            {successLabel}
          </>
        ) : (
          <>
            <Send className="h-4 w-4" />
            {submitLabel}
          </>
        )}
      </button>
    </form>
  );
}

export function MarketingUseDisclosure({
  checked,
  dark,
  text,
  onChange,
}: {
  checked: boolean;
  dark: boolean;
  text: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 rounded border-border text-em-blue focus:ring-em-blue/50"
      />
      <span className={`text-xs leading-relaxed ${dark ? "text-white/70" : "text-muted-foreground"}`}>
        {text}
      </span>
    </label>
  );
}

export function MarketingOptIns({
  optIns,
  selectedTopicIds,
  dark = false,
  className = "",
  onChange,
}: {
  optIns: MarketingFormOptIn[];
  selectedTopicIds: number[];
  dark?: boolean;
  className?: string;
  onChange: (topicIds: number[]) => void;
}) {
  if (optIns.length === 0) return null;

  return (
    <fieldset className={`space-y-3 ${className}`}>
      <legend className="sr-only">Marketing preferences</legend>
      {optIns.map((optIn) => {
        const checked = selectedTopicIds.includes(optIn.topic_id);
        return (
          <label key={optIn.topic_id} className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              name="marketing_opt_ins"
              value={optIn.topic_id}
              required={optIn.required}
              checked={checked}
              onChange={(event) => onChange(
                event.target.checked
                  ? Array.from(new Set([...selectedTopicIds, optIn.topic_id]))
                  : selectedTopicIds.filter((topicId) => topicId !== optIn.topic_id),
              )}
              className="mt-1 h-4 w-4 rounded border-border text-em-blue focus:ring-em-blue/50"
            />
            <span className={`text-xs leading-relaxed ${dark ? "text-white/70" : "text-muted-foreground"}`}>
              {optIn.opt_in_copy}
              {optIn.required && <span aria-hidden="true"> *</span>}
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}

function DynamicField({
  field,
  value,
  inputClass,
  dark,
  onChange,
}: {
  field: MarketingFormField;
  value: FieldValue | undefined;
  inputClass: string;
  dark: boolean;
  onChange: (value: FieldValue) => void;
}) {
  const placeholder = field.placeholder || field.label;
  const required = field.required;
  const textClass = dark ? "text-white/70" : "text-muted-foreground";
  const autoComplete = getContactFieldAutoComplete(field);

  if (field.field_type === "textarea") {
    return (
      <textarea
        name={field.field_key}
        rows={5}
        required={required}
        placeholder={placeholder}
        value={String(value ?? "")}
        minLength={field.min_length ?? undefined}
        maxLength={field.max_length ?? undefined}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputClass} resize-none`}
      />
    );
  }

  if (field.field_type === "select") {
    return (
      <select
        name={field.field_key}
        required={required}
        value={String(value ?? "")}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputClass} ${!value ? "text-muted-foreground" : ""}`}
      >
        <option value="" disabled={required}>
          {placeholder}
        </option>
        {field.options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }

  if (field.field_type === "radio") {
    return (
      <fieldset className="space-y-2">
        <legend className={`text-sm font-medium ${textClass}`}>{field.label}{required ? " *" : ""}</legend>
        {field.options.map((option) => (
          <label key={option} className={`flex items-center gap-2 text-sm ${textClass}`}>
            <input
              type="radio"
              name={field.field_key}
              required={required}
              checked={value === option}
              onChange={() => onChange(option)}
              className="h-4 w-4 border-border text-em-blue focus:ring-em-blue/50"
            />
            {option}
          </label>
        ))}
      </fieldset>
    );
  }

  if (field.field_type === "checkbox") {
    const selected = Array.isArray(value) ? value : [];
    return (
      <fieldset className="space-y-2">
        <legend className={`text-sm font-medium ${textClass}`}>{field.label}{required ? " *" : ""}</legend>
        {field.options.map((option) => (
          <label key={option} className={`flex items-center gap-2 text-sm ${textClass}`}>
            <input
              type="checkbox"
              name={field.field_key}
              checked={selected.includes(option)}
              onChange={(e) => {
                onChange(e.target.checked ? [...selected, option] : selected.filter((item) => item !== option));
              }}
              className="h-4 w-4 rounded border-border text-em-blue focus:ring-em-blue/50"
            />
            {option}
          </label>
        ))}
      </fieldset>
    );
  }

  if (field.field_type === "consent") {
    return (
      <label className="flex cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          name={field.field_key}
          required={required}
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-border text-em-blue focus:ring-em-blue/50"
        />
        <span className={`text-xs leading-relaxed ${textClass}`}>{field.label}</span>
      </label>
    );
  }

  const type = field.field_type === "phone" ? "tel" : field.field_type === "zip" ? "text" : field.field_type;
  return (
    <input
      type={type}
      name={field.field_key}
      autoComplete={autoComplete}
      required={required}
      placeholder={placeholder}
      value={String(value ?? "")}
      minLength={field.min_length ?? undefined}
      maxLength={field.max_length ?? undefined}
      onChange={(e) => onChange(e.target.value)}
      className={inputClass}
    />
  );
}

function initialValues(fields: MarketingFormField[]) {
  return Object.fromEntries(
    fields.map((field) => [
      field.field_key,
      field.field_type === "checkbox" ? [] : field.field_type === "consent" ? false : "",
    ]),
  );
}
